// Lazy migration helpers für Multi-Voice (#173). Idempotent + read-time only —
// kein KV-Bulk-Migration-Job. Wird einmal im Config-Loader und an den
// Hydrate-Pfaden (selectAllAssets) angewendet.
//
// Seit 2026-09-24 (Audit M10) kappen die Resolver zusätzlich auf die aktuell
// erlaubten Voices des Assets: Tauscht der Admin die Voice einer Sprache,
// tragen bestehende Blöcke (und ihre Takes) noch die alte ID. Ohne Kappung
// ging der nächste Generate mit der alten Voice raus (bzw. 403), und die
// Timeline gruppierte die Spur nach der alten Voice — Chips überlappten
// unsichtbar, doppelte Sprache landete im Mix.

import type { Asset, Block, SessionState, Take } from '@shared/types';

/** Promotes `Asset.voiceId` (legacy) zu `voiceIds: [id]`. Idempotent —
 *  Assets mit bereits gesetztem voiceIds-Array bleiben unverändert.
 *  Legacy `voiceId` wird im Ergebnis entfernt damit beim nächsten Save
 *  das Feld nicht zurück in den KV-Record läuft. */
export function applyVoiceIdsMigration(assets: Asset[]): Asset[] {
  return assets.map((a) => {
    if (a.voiceIds && a.voiceIds.length > 0) {
      if (a.voiceId === undefined) return a;
      const { voiceId, ...rest } = a;
      return rest as Asset;
    }
    if (a.voiceId) {
      const { voiceId, ...rest } = a;
      return { ...rest, voiceIds: [voiceId] } as Asset;
    }
    return a;
  });
}

/** Primary voice (=Spur-1-voice) für ein Asset. Bevorzugt das neue
 *  voiceIds-Array; fällt auf das deprecated voiceId-Feld zurück für
 *  in-flight unmigrated reads. Leerstring wenn weder noch. */
export function getPrimaryVoiceId(
  asset: { voiceId?: string; voiceIds?: string[] } | null | undefined,
): string {
  if (!asset) return '';
  if (asset.voiceIds && asset.voiceIds.length > 0) return asset.voiceIds[0];
  return asset.voiceId ?? '';
}

/** Ist `voiceId` für dieses Asset (noch) erlaubt? Eine leere Liste kennt
 *  keine Einschränkung (Custom-Asset vor der Voice-Wahl). */
function isAllowedVoice(voiceId: string | undefined, voiceIds: string[]): boolean {
  if (!voiceId) return false;
  return voiceIds.length === 0 || voiceIds.includes(voiceId);
}

/** Resolve `block.voiceId`, gekappt auf die erlaubten Asset-Voices: eine
 *  veraltete Voice (Admin-Tausch) fällt auf `voiceIds[0]` zurück. */
export function resolveBlockVoiceId(block: Pick<Block, 'voiceId'>, voiceIds: string[]): string {
  if (isAllowedVoice(block.voiceId, voiceIds)) return block.voiceId;
  return voiceIds[0] ?? block.voiceId ?? '';
}

/** Resolve `take.voiceId` mit Fallback auf den Block-Voice-Fallback. Mit
 *  `voiceIds` wird auch eine veraltete Take-Voice auf den Fallback gekappt
 *  (Spurgruppierung nur über gültige Voices). */
export function resolveTakeVoiceId(
  take: Pick<Take, 'voiceId'>,
  blockVoiceIdFallback: string,
  voiceIds?: string[],
): string {
  if (!take.voiceId) return blockVoiceIdFallback;
  if (voiceIds && !isAllowedVoice(take.voiceId, voiceIds)) return blockVoiceIdFallback;
  return take.voiceId;
}

/** Schreibt veraltete `block.voiceId` auf die erlaubte Voice um. Blöcke, die
 *  schon passen, behalten ihre Referenz. */
export function clampBlockVoiceIds(blocks: Block[], voiceIds: string[]): Block[] {
  if (voiceIds.length === 0) return blocks;
  let changed = false;
  const next = blocks.map((b) => {
    const resolved = resolveBlockVoiceId(b, voiceIds);
    if (resolved === b.voiceId) return b;
    changed = true;
    return { ...b, voiceId: resolved };
  });
  return changed ? next : blocks;
}

/** Restore-Kappung über die ganze Session: `assetBlocks[idx]` und
 *  `freeBlocks[idx].tts` gegen die Voices des Assets an `idx` (kombinierte
 *  Reihenfolge Config-Assets + Custom-Assets). */
export function clampSessionBlockVoices(
  s: SessionState,
  assets: ReadonlyArray<Pick<Asset, 'voiceIds' | 'voiceId'>>,
): SessionState {
  const voicesAt = (idx: number): string[] => {
    const a = assets[idx];
    if (!a) return [];
    if (a.voiceIds && a.voiceIds.length > 0) return a.voiceIds;
    return a.voiceId ? [a.voiceId] : [];
  };
  let changed = false;
  const assetBlocks: Record<number, Block[]> = {};
  for (const [k, blocks] of Object.entries(s.assetBlocks ?? {})) {
    const idx = Number(k);
    const next = clampBlockVoiceIds(blocks, voicesAt(idx));
    if (next !== blocks) changed = true;
    assetBlocks[idx] = next;
  }
  const freeBlocks: SessionState['freeBlocks'] = {};
  for (const [k, modes] of Object.entries(s.freeBlocks ?? {})) {
    const idx = Number(k);
    const tts = clampBlockVoiceIds(modes?.tts ?? [], voicesAt(idx));
    if (tts !== (modes?.tts ?? [])) changed = true;
    freeBlocks[idx] = { tts, sts: modes?.sts ?? [] };
  }
  if (!changed) return s;
  return {
    ...s,
    assetBlocks,
    freeBlocks,
    blocks: assetBlocks[s.currentAssetIdx] ?? s.blocks,
  };
}
